import { Edge } from '@xyflow/react'

import { IActionType } from '@makyoapp/makyo-common'

import { NODE_STYLES } from '../constants'
import { WorkflowEdge, WorkflowNode } from '../types'
import { WorkflowNodeManager } from './WorkflowNodeManager'

export interface EdgeConnection {
  sourceId: string
  targetId: string
  sourceHandle?: string
  targetHandle?: string
}

export interface InsertBetweenInfo {
  edgeId: string // ID of the edge being broken
  targetNodeId: string // Target node of the original edge (will be target of new edge)
  sourceBranch?: string | null // Branch information from the source node
  edgeInfo?: {
    sourceX: number
    sourceY: number
    targetX: number
    targetY: number
  }
}

export class WorkflowEdgeManager {
  /**
   * Creates a new edge between two nodes
   */
  static createEdge(sourceId: string, targetId: string, sourceHandle?: string, targetHandle?: string): WorkflowEdge {
    return {
      id: `e${sourceId}-${targetId}`,
      source: sourceId,
      target: targetId,
      type: 'workflowEdge',
      style: NODE_STYLES.edge.default,
      sourceHandle,
      targetHandle,
    }
  }

  /**
   * Creates an edge with a custom ID
   */
  static createEdgeWithId(
    id: string,
    sourceId: string,
    targetId: string,
    sourceHandle?: string,
    targetHandle?: string
  ): WorkflowEdge {
    return {
      id,
      source: sourceId,
      target: targetId,
      type: 'workflowEdge',
      style: NODE_STYLES.edge.default,
      sourceHandle,
      targetHandle,
    }
  }

  /**
   * Creates edges for inserting a node between existing nodes
   */
  static createInsertBetweenEdges(
    sourceId: string,
    newNode: WorkflowNode,
    targetId: string,
    insertInfo: InsertBetweenInfo,
    newNodeActionType?: IActionType
  ): { sourceToNew: WorkflowEdge; newToTarget: WorkflowEdge } {
    const newNodeId = newNode.id
    // Create edge from source to new node
    const sourceToNew = this.createEdge(
      sourceId,
      newNodeId,
      insertInfo.sourceBranch !== null && insertInfo.sourceBranch !== undefined
        ? WorkflowNodeManager.getSourceHandle(sourceId, insertInfo.sourceBranch)
        : undefined
    )

    // Create edge from new node to target
    let newToTarget: WorkflowEdge
    if (newNodeActionType === IActionType.ConditionalIf) {
      newToTarget = this.createEdge(
        newNodeId,
        targetId,
        WorkflowNodeManager.getSourceHandle(newNodeId, 'true') // Connect to true branch
      )
    } else if (newNodeActionType === IActionType.ConditionalSwitch) {
      const firstCaseId = newNode?.data.actionValue?.settings?.cases?.[0]?.id
      newToTarget = this.createEdge(
        newNodeId,
        targetId,
        WorkflowNodeManager.getSourceHandle(newNodeId, firstCaseId) // Connect to first case
      )
    } else if (newNodeActionType === IActionType.AddToCart) {
      newToTarget = this.createEdge(
        newNodeId,
        targetId,
        WorkflowNodeManager.getSourceHandle(newNodeId, 'insideCart') // Connect to inside cart branch
      )
    } else {
      newToTarget = this.createEdge(newNodeId, targetId)
    }

    return { sourceToNew, newToTarget }
  }

  /**
   * Finds edges connected to a specific node
   */
  static findConnectedEdges(
    edges: WorkflowEdge[],
    nodeId: string
  ): {
    incoming: WorkflowEdge[]
    outgoing: WorkflowEdge[]
  } {
    const incoming = edges.filter((edge) => edge.target === nodeId)
    const outgoing = edges.filter((edge) => edge.source === nodeId)
    return { incoming, outgoing }
  }

  /**
   * Finds edges for a specific branch of a conditional node
   */
  static findBranchEdges(edges: WorkflowEdge[], nodeId: string, branch: string): WorkflowEdge[] {
    const sourceHandle = WorkflowNodeManager.getSourceHandle(nodeId, branch)
    return edges.filter((edge) => edge.source === nodeId && edge.sourceHandle === sourceHandle)
  }

  /**
   * Removes edges connected to a specific node
   */
  static removeNodeEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
    return edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
  }

  /**
   * Removes edges connected to a specific node except for the ones in the branchIds list
   */
  static removeEdgesByWhiteListBranchIds(edges: WorkflowEdge[], nodeId: string, branchIds: string[]): WorkflowEdge[] {
    return edges.filter((edge) => {
      // Keep edges that are not from the specified node
      if (edge.source !== nodeId) {
        return true
      }

      // For edges from the specified node, only keep those whose sourceHandle is in the branchIds list
      return branchIds.includes(edge.sourceHandle || '')
    })
  }

  /**
   * Removes a specific edge by ID
   */
  static removeEdge(edges: WorkflowEdge[], edgeId: string): WorkflowEdge[] {
    return edges.filter((edge) => edge.id !== edgeId)
  }

  /**
   * Validates if a connection is valid (no duplicate incoming edges)
   */
  static isValidConnection(edges: Array<WorkflowEdge | Edge>, targetNodeId: string): boolean {
    const existingIncomingEdges = edges.filter((edge) => edge.target === targetNodeId)
    return existingIncomingEdges.length === 0
  }

  /**
   * Gets all outgoing edges for a node
   */
  static getOutgoingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
    return edges.filter((edge) => edge.source === nodeId)
  }

  /**
   * Gets all incoming edges for a node
   */
  static getIncomingEdges(edges: WorkflowEdge[], nodeId: string): WorkflowEdge[] {
    return edges.filter((edge) => edge.target === nodeId)
  }

  /**
   * Checks if a node has any outgoing connections
   */
  static hasOutgoingConnections(edges: WorkflowEdge[], nodeId: string): boolean {
    return this.getOutgoingEdges(edges, nodeId).length > 0
  }
}
